import { Autocomplete, Button, Flex, FormStatus, P, Section, Table, Td, Th, Tr } from '@dnb/eufemia';
import { Form } from '@dnb/eufemia/extensions/forms';
import { areSetsEqual } from '@portals/shared/common/utils';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useBackend } from '@/hooks/useBackend';
import { ApiVisibilityResponse, OrganizationVisibility, TeamVisibility } from '@/openapi/schema';
import { DropdownOption } from '@/pages/api-documentation/constants/types';
import { SwitchContainerModeRef } from '@/types/SwitchContainerModeTypes';

import { ApiProviderPageOutletContext } from '../..';
import { FormCancelButton } from '../../components/FormCancelButton';
import FormSubmitButton from '../../components/FormSubmitButton';
import { dnbOrganization, VisibilityStates } from './visibilityTypes';

import style from './EditContainer.module.css';

interface EditContainerProps {
  errorMessage: string | null;
  switchContainerModeRef: SwitchContainerModeRef;
  dnbOrganization: dnbOrganization;
  currentConfig: ApiVisibilityResponse;
}

export function EditContainer({
  errorMessage,
  dnbOrganization,
  currentConfig,
  switchContainerModeRef,
}: EditContainerProps) {
  const { api } = useOutletContext<ApiProviderPageOutletContext>();
  const { data: teamList } = useBackend('/organizations/{organizationId}/teams', {
    params: { path: { organizationId: dnbOrganization.id } },
  });

  const { getValue, update, set } = Form.useData();

  const [teamAutoInputValue, setTeamAutoInput] = useState('');
  const [orgAutoInputValue, setOrgAutoInput] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const organizations: OrganizationVisibility[] = getValue('/organizations') ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const teams: TeamVisibility[] = getValue('/teams') ?? [];

  /**
   * availableOrganizations consists of the DNB organization and eventual organizations that the API has already been
   * shared with.
   */
  const availableOrganizations = useMemo(() => {
    return currentConfig.organizations.some((currentOrg) => currentOrg.id === dnbOrganization.id)
      ? currentConfig.organizations
      : [...currentConfig.organizations, dnbOrganization];
  }, [currentConfig.organizations, dnbOrganization]);

  const selectableOrganizations: DropdownOption[] = useMemo(() => {
    const selectedOrganizationIds = new Set(organizations.map((org) => org.id));
    return availableOrganizations
      .filter(({ id }) => !selectedOrganizationIds.has(id))
      .map((org) => ({ content: [org.name, org.id], key: org.id }));
  }, [availableOrganizations, organizations]);

  /**
   * availableTeams consists of the teams that the API has already been shared with, and the teams that are available
   * in the DNB organization.
   */
  const availableTeams = useMemo(() => {
    if (!teamList) return currentConfig.teams;
    const teamIdToTeam = new Map([...currentConfig.teams, ...teamList].map((team) => [team.id, team]));
    return Array.from(teamIdToTeam.values());
  }, [currentConfig.teams, teamList]);

  const selectableTeams = useMemo(() => {
    const selectedTeamIds = new Set(teams.map((team) => team.id));
    return availableTeams
      .filter((team) => !selectedTeamIds.has(team.id))
      .map((team) => ({ content: [team.name, team.id], key: team.id }));
  }, [availableTeams, teams]);

  const hasChanges = useMemo(() => {
    const organizationIds = !areSetsEqual(
      new Set(organizations.map((org) => org.id)),
      new Set(currentConfig.organizations.map((o) => o.id)),
    );
    const teamIdsChanged = !areSetsEqual(
      new Set(teams.map((t) => t.id)),
      new Set(currentConfig.teams.map((t) => t.id)),
    );
    return organizationIds || teamIdsChanged;
  }, [currentConfig.organizations, currentConfig.teams, organizations, teams]);

  const handleAddOrganization = (selected: DropdownOption) => {
    if (organizations.some((org) => org.id === selected?.key) || !Array.isArray(selected.content)) {
      return;
    }
    const updatedOrgs = [...organizations, { id: selected.key, name: selected.content[0] }];
    update('/organizations', updatedOrgs);
  };

  const handleAddTeam = (selected: DropdownOption) => {
    if (teams.some((team) => team.id === selected?.key) || !Array.isArray(selected.content)) {
      return;
    }

    const updatedTeams = [...teams, { id: selected.key, name: selected.content[0] }];
    update('/teams', updatedTeams);
  };

  const handleRemoveOrganization = (id: string) => {
    const updatedOrgs = organizations.filter((org) => org.id !== id);
    update('/organizations', updatedOrgs);
  };

  const handleRemoveTeam = (id: string) => {
    const updatedTeams = teams.filter((team) => team.id !== id);
    update('/teams', updatedTeams);
  };

  const clearInputFields = () => {
    setTeamAutoInput('');
    setOrgAutoInput('');
  };

  const getVisibilityInfoMessage = useMemo(() => {
    if (api.type === 'EVENT') return null;
    switch (api.classification) {
      case 'public': {
        return 'This API is classified as public, but is not publicly visible. Contact the Portal team to enable public visibility.';
      }
      case 'partner': {
        return 'This API is classified as a partner API. To add external organizations and teams, contact the Portal team.';
      }
      default: {
        return null;
      }
    }
  }, [api.type, api.classification]);

  const visibilityInfoMessage = getVisibilityInfoMessage;

  return (
    <Form.Section.EditContainer>
      <Form.MainHeading>Visibility Settings</Form.MainHeading>
      {visibilityInfoMessage && <FormStatus state="info" stretch text={visibilityInfoMessage} />}
      {organizations.length === 0 && teams.length === 0 && (
        <Section innerSpace={{ top: 'small', bottom: 'small' }} top="small" variant="warning">
          <P>{VisibilityStates.NONE}</P>
        </Section>
      )}
      <Form.SubHeading bottom="small">Organizations</Form.SubHeading>
      {organizations.length > 0 && (
        <Table outline>
          <thead>
            <Tr>
              <Th>Name</Th>
            </Tr>
          </thead>
          <tbody>
            {organizations.map(({ id, name }) => (
              <Tr key={id}>
                <Td>
                  <Flex.Horizontal justify="space-between">
                    {name}
                    <Button
                      className={style['subtractButton']}
                      icon="subtract"
                      onClick={() => handleRemoveOrganization(id)}
                      title="Remove organization"
                      wrap
                    />
                  </Flex.Horizontal>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Flex.Horizontal align="center" gap="small" top="small">
        <Autocomplete
          data={selectableOrganizations}
          input_value={orgAutoInputValue}
          on_change={({ data }) => {
            handleAddOrganization(data);
          }}
          on_type={({ value }) => setOrgAutoInput(value)}
          placeholder="Add organization"
          prevent_selection
          stretch
        />
      </Flex.Horizontal>

      <Form.SubHeading bottom="small">Teams</Form.SubHeading>
      {teams.length > 0 && (
        <Table outline>
          <thead>
            <Tr>
              <Th>Name</Th>
            </Tr>
          </thead>
          <tbody>
            {teams.map(({ id, name }) => (
              <Tr key={id}>
                <Td>
                  <Flex.Horizontal justify="space-between">
                    {name}
                    <Button
                      className={style['subtractButton']}
                      icon="subtract"
                      onClick={() => handleRemoveTeam(id)}
                      title="Remove team"
                      wrap
                    />
                  </Flex.Horizontal>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Flex.Horizontal align="center" gap="small" top="small">
        <Autocomplete
          data={selectableTeams}
          input_value={teamAutoInputValue}
          on_change={({ data }) => {
            handleAddTeam(data);
          }}
          on_type={({ value }) => setTeamAutoInput(value)}
          placeholder="Add team"
          prevent_selection
          stretch
        />
      </Flex.Horizontal>
      {errorMessage && <FormStatus state="error" text={errorMessage} />}

      <Form.Section.Toolbar>
        <FormSubmitButton
          disabled={!hasChanges}
          onClick={() => clearInputFields()}
          switchContainerModeRef={switchContainerModeRef}
        />
        <FormCancelButton
          onCancel={() => {
            clearInputFields();
            set(currentConfig);
          }}
          switchContainerModeRef={switchContainerModeRef}
        />
      </Form.Section.Toolbar>
    </Form.Section.EditContainer>
  );
}
